/* eslint-disable max-len */
import { Formik, useFormikContext } from 'formik';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useConfirmationModalContext } from '../../../hooks/useConfirmationModalContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectLiteracyScreeningFiles,
  selectLiteracyScreeningOptionSets,
  selectLiteracyScreeningSchoolList,
  selectLiteracyScreeningUpdate,
} from '../../../redux/slices/literacyScreeningSlice';
import {
  deleteLiteracyScreeningFileThunk,
  fetchLiteracyScreeningFilesByLeaThunk,
  updateLiteracyScreeningDocumentThunk,
  uploadLiteracyScreeningFileThunk,
} from '../../../redux/thunks/literacyScreeningThunks';
import {
  getMaxFileUploadSizeMB,
  getMaxUploadFilenameLength,
} from '../../../services/fileUpload.service';
import { downloadLiteracyScreeningFile } from '../../../services/literacyScreening.service';
import {
  canDeleteLiteracyScreeningUploadedFile,
  createLiteracyScreeningUploadValidationSchema,
  getEnabledLiteracyScreeningOptions,
  getLiteracyScreeningDisplayedMdeStatus,
  getLiteracyScreeningDocumentDetailLines,
  getLiteracyScreeningUploadDocumentType,
  getLiteracyScreeningUploadFieldOptions,
  isLiteracyScreeningDeniedStatus,
  isLiteracyScreeningLeaInvoiceLevel,
  isLiteracyScreeningSchoolInvoiceLevel,
  isLiteracyScreeningScreenerInvoiceDocumentType,
  isLiteracyScreeningSupportingDocumentType,
  LITERACY_SCREENING_COMMENTS_MAX_LENGTH,
  LITERACY_SCREENING_DEFAULT_MDE_STATUS_OPTIONS,
  LITERACY_SCREENING_DOCUMENT_OPTION_SETS,
  LITERACY_SCREENING_PENDING_APPROVAL_STATUS,
  mergeLiteracyScreeningMdeStatusOptions,
  sortLiteracyScreeningFilesNewestFirst,
} from '../../../types/LiteracyScreeningDocumentUpload';
import { LiteracyScreeningFileListItemModel } from '../../../types/LiteracyScreeningFileListItemModel';
import { PanelType } from '../../../types/PanelType';
import { SelectOption } from '../../../types/SelectOption';
import { VALIDATION_MESSAGE } from '../../../types/ValidationMessage';
import { formatToDateString } from '../../../utilities/dateUtilities';
import { createDownloadLinkAndExecute } from '../../../utilities/formUtilities';
import {
  isDistrictUser,
  isSPO,
  isStateAdmin,
  isStateHelpdesk,
} from '../../../utilities/userUtilities';
import ActionButton from '../../ActionButton/ActionButton';
import FileUploadStatus from '../../FileUploadStatus/FileUploadStatus';
import FormDropDown from '../../FormInputs/FormDropdown/FormDropdown';
import Panel from '../../Panel/Panel';
import './LiteracyScreeningUploadDocuments.css';

interface FileUploadState {
  progress: number;
  isCompleted: boolean;
  isFailed: boolean;
  error?: string;
}

interface LiteracyScreeningUploadFormValues {
  documentType: string;
  invoiceLevel: string;
  school: string;
  screenerInvoice: string;
}

interface LiteracyScreeningUploadDocumentsProps {
  schoolYear?: string;
  leaIdentifier?: string;
  reimbursement?: string;
  disabled?: boolean;
}

const EMPTY_UPLOAD_VALUES: LiteracyScreeningUploadFormValues = {
  documentType: '',
  invoiceLevel: '',
  school: '',
  screenerInvoice: '',
};

const toUploadFieldValue = (value?: string | null): string =>
  (value || '').trim();

const noopSubmit = (): void => {
  void 0;
};

const getLastSegmentFromFilename = (filename: string): string => {
  const parts = filename.split(';');
  return parts[parts.length - 1];
};

const formatUploadDate = (uploadedDate?: string): string => {
  if (!uploadedDate) {
    return 'Unknown';
  }
  const formatted = formatToDateString(uploadedDate);
  if (formatted) {
    return formatted;
  }
  return uploadedDate.split(' ')[0] || uploadedDate;
};

const mapUploadError = (errorMessage: string): string => {
  if (
    errorMessage.includes('500') ||
    errorMessage.includes('Internal Server Error')
  ) {
    return 'The server encountered an error while processing your file. Please try again.';
  }
  if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
    return 'The file could not be processed. Please check the file format and try again.';
  }
  if (
    errorMessage.includes('413') ||
    errorMessage.includes('Request Entity Too Large')
  ) {
    return 'The file is too large. Please choose a smaller file.';
  }
  if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
    return 'The upload timed out. Please try again.';
  }
  return errorMessage;
};

const validatePdfFile = (file: File): string => {
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf('.'));

  if (fileExtension !== '.pdf') {
    return 'Invalid file type, must be .pdf';
  }

  const maxSize = getMaxFileUploadSizeMB() * 1024 * 1024;
  if (file.size > maxSize) {
    return 'Files cannot be larger than 50 MB';
  }

  if (file.name.length > getMaxUploadFilenameLength()) {
    return 'Filename exceeds max length of 50 characters';
  }

  return '';
};

const LiteracyScreeningUploadDocumentsForm = ({
  schoolYear,
  leaIdentifier,
  reimbursement = '',
  disabled = false,
}: LiteracyScreeningUploadDocumentsProps): ReactElement => {
  const dispatch = useAppDispatch();
  const { values, resetForm, setFieldTouched, setFieldValue } =
    useFormikContext<LiteracyScreeningUploadFormValues>();
  const optionSets = useAppSelector(selectLiteracyScreeningOptionSets);
  const schoolList = useAppSelector(selectLiteracyScreeningSchoolList);
  const filesToUse = useAppSelector(selectLiteracyScreeningFiles);
  const literacyScreeningUpdate = useAppSelector(selectLiteracyScreeningUpdate);
  const reduxError = useAppSelector((state) => state.literacyScreening.error);
  const { showConfirmation, setOptions } = useConfirmationModalContext();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [uploadState, setUploadState] = useState<FileUploadState>({
    progress: 0,
    isCompleted: false,
    isFailed: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>(
    {}
  );
  const [statusDrafts, setStatusDrafts] = useState<Record<string, string>>({});
  const [commentErrors, setCommentErrors] = useState<Record<string, string>>(
    {}
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rawDocumentTypeOptions = useMemo(
    () =>
      getEnabledLiteracyScreeningOptions(
        optionSets,
        LITERACY_SCREENING_DOCUMENT_OPTION_SETS.documentType
      ),
    [optionSets]
  );
  const rawInvoiceLevelOptions = useMemo(
    () =>
      getEnabledLiteracyScreeningOptions(
        optionSets,
        LITERACY_SCREENING_DOCUMENT_OPTION_SETS.invoiceLevel
      ),
    [optionSets]
  );
  const rawScreenerInvoiceOptions = useMemo(
    () =>
      getEnabledLiteracyScreeningOptions(
        optionSets,
        LITERACY_SCREENING_DOCUMENT_OPTION_SETS.screenerInvoice
      ),
    [optionSets]
  );
  const mdeStatusOptions = useMemo(() => {
    const options = getEnabledLiteracyScreeningOptions(
      optionSets,
      LITERACY_SCREENING_DOCUMENT_OPTION_SETS.mdeStatus
    );
    const merged = mergeLiteracyScreeningMdeStatusOptions(
      options.length > 0
        ? options
        : LITERACY_SCREENING_DEFAULT_MDE_STATUS_OPTIONS,
      filesToUse
    );
    const preferred = [
      LITERACY_SCREENING_PENDING_APPROVAL_STATUS,
      'Approved',
      'Denied',
    ];
    return preferred.map((status) => {
      const match = merged.find(
        (option) => option.value.toLowerCase() === status.toLowerCase()
      );
      return match || { value: status, text: status, isDisabled: false };
    });
  }, [filesToUse, optionSets]);
  const uploadedFiles = useMemo(
    () => sortLiteracyScreeningFilesNewestFirst(filesToUse),
    [filesToUse]
  );
  const fieldOptions = useMemo(
    () =>
      getLiteracyScreeningUploadFieldOptions({
        documentTypeOptions: rawDocumentTypeOptions,
        invoiceLevelOptions: rawInvoiceLevelOptions,
        screenerInvoiceOptions: rawScreenerInvoiceOptions,
        schools: schoolList,
        files: filesToUse,
        reimbursement,
        selectedInvoiceLevel: values.invoiceLevel,
        selectedSchool: values.school,
      }),
    [
      filesToUse,
      rawDocumentTypeOptions,
      rawInvoiceLevelOptions,
      rawScreenerInvoiceOptions,
      reimbursement,
      schoolList,
      values.invoiceLevel,
      values.school,
    ]
  );
  const validationSchema = useMemo(
    () =>
      createLiteracyScreeningUploadValidationSchema(
        rawDocumentTypeOptions,
        rawInvoiceLevelOptions
      ),
    [rawDocumentTypeOptions, rawInvoiceLevelOptions]
  );

  const documentType = toUploadFieldValue(values.documentType);
  const invoiceLevel = toUploadFieldValue(values.invoiceLevel);
  const school = toUploadFieldValue(values.school);
  const screenerInvoice = toUploadFieldValue(values.screenerInvoice);
  const isScreenerInvoiceSelected =
    isLiteracyScreeningScreenerInvoiceDocumentType(
      documentType,
      rawDocumentTypeOptions
    );
  const isSchoolLevelSelected = isLiteracyScreeningSchoolInvoiceLevel(
    invoiceLevel,
    rawInvoiceLevelOptions
  );
  const isLeaLevelSelected = isLiteracyScreeningLeaInvoiceLevel(
    invoiceLevel,
    rawInvoiceLevelOptions
  );
  const isScreenerInvoiceActivated =
    isScreenerInvoiceSelected &&
    (isLeaLevelSelected || (isSchoolLevelSelected && Boolean(school)));
  const requiredLabelClass = isScreenerInvoiceSelected
    ? 'forced-required-label'
    : '';
  const schoolRequiredLabelClass =
    isScreenerInvoiceSelected && isSchoolLevelSelected
      ? 'forced-required-label'
      : '';
  const screenerRequiredLabelClass = isScreenerInvoiceActivated
    ? 'forced-required-label'
    : '';

  const canUpload = Boolean(schoolYear?.trim() && leaIdentifier?.trim());
  const uploadControlsDisabled =
    disabled ||
    !literacyScreeningUpdate ||
    !canUpload;
  const invoiceFieldsDisabled =
    uploadControlsDisabled || !isScreenerInvoiceSelected;
  const schoolFieldDisabled = invoiceFieldsDisabled || !isSchoolLevelSelected;
  const screenerInvoiceFieldDisabled =
    invoiceFieldsDisabled || !isScreenerInvoiceActivated;
  const requiredFieldsComplete =
    Boolean(documentType) &&
    (!isScreenerInvoiceSelected ||
      (Boolean(invoiceLevel) &&
        Boolean(screenerInvoice) &&
        (!isSchoolLevelSelected || Boolean(school))));
  const fileChooserDisabled = uploadControlsDisabled || !requiredFieldsComplete;
  const isRestrictedDocumentDeleteUser = isSPO() || isDistrictUser();
  const cannotDeleteDocuments = isStateHelpdesk();

  useEffect(() => {
    setCommentDrafts((previous) => {
      const next = { ...previous };
      filesToUse.forEach((file) => {
        if (next[file.id] === undefined) {
          next[file.id] = file.comments || '';
        }
      });
      return next;
    });
    setStatusDrafts((previous) => {
      const next = { ...previous };
      filesToUse.forEach((file) => {
        if (next[file.id] === undefined) {
          next[file.id] = getLiteracyScreeningDisplayedMdeStatus(
            file.mdeStatus
          );
        }
      });
      return next;
    });
  }, [filesToUse]);

  useEffect(() => {
    const selectedDocumentType = fieldOptions.documentTypeOptions.find(
      (option) => option.value === values.documentType
    );
    if (
      values.documentType &&
      (!selectedDocumentType || selectedDocumentType.isDisabled)
    ) {
      void setFieldValue('documentType', '');
      void setFieldValue('invoiceLevel', '');
      void setFieldValue('school', '');
      void setFieldValue('screenerInvoice', '');
      return;
    }

    const selectedInvoiceLevel = fieldOptions.invoiceLevelOptions.find(
      (option) => option.value === values.invoiceLevel
    );
    if (
      values.invoiceLevel &&
      (!selectedInvoiceLevel || selectedInvoiceLevel.isDisabled)
    ) {
      void setFieldValue('invoiceLevel', '');
      void setFieldValue('school', '');
      void setFieldValue('screenerInvoice', '');
      return;
    }

    const selectedSchool = fieldOptions.schoolOptions.find(
      (option) => option.value === values.school
    );
    if (values.school && (!selectedSchool || selectedSchool.isDisabled)) {
      void setFieldValue('school', '');
      void setFieldValue('screenerInvoice', '');
      return;
    }

    const selectedScreener = fieldOptions.screenerInvoiceOptions.find(
      (option) => option.value === values.screenerInvoice
    );
    if (
      values.screenerInvoice &&
      (!selectedScreener || selectedScreener.isDisabled)
    ) {
      void setFieldValue('screenerInvoice', '');
    }
  }, [
    fieldOptions.documentTypeOptions,
    fieldOptions.invoiceLevelOptions,
    fieldOptions.schoolOptions,
    fieldOptions.screenerInvoiceOptions,
    setFieldValue,
    values.documentType,
    values.invoiceLevel,
    values.school,
    values.screenerInvoice,
  ]);

  useEffect(() => {
    void setFieldTouched('invoiceLevel', isScreenerInvoiceSelected, false);
  }, [isScreenerInvoiceSelected, setFieldTouched]);

  useEffect(() => {
    void setFieldTouched('school', isSchoolLevelSelected, false);
  }, [isSchoolLevelSelected, setFieldTouched]);

  useEffect(() => {
    void setFieldTouched('screenerInvoice', isScreenerInvoiceActivated, false);
  }, [isScreenerInvoiceActivated, setFieldTouched]);

  const handleClearFile = (): void => {
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setFileError(
        'File is too large for the browser to handle. Please try a smaller file.'
      );
      return;
    }

    if (file.size === 0) {
      setSelectedFile(file);
      setFileError(
        'File appears to be empty or corrupted. Please try a different file.'
      );
      return;
    }

    const error = validatePdfFile(file);
    setFileError(error);
    setSelectedFile(file);
  };

  const handleChooseFiles = (): void => {
    setFileError('');
    fileInputRef.current?.click();
  };

  const handleTryAgain = (): void => {
    handleClearFile();
    setUploadState({
      progress: 0,
      isCompleted: false,
      isFailed: false,
    });
    setIsUploading(false);
  };

  const refetchFiles = async (showLoading = false): Promise<void> => {
    if (!schoolYear || !leaIdentifier) {
      return;
    }

    await dispatch(
      fetchLiteracyScreeningFilesByLeaThunk({
        leaId: leaIdentifier,
        schoolYear,
        showLoading,
      })
    );
  };

  const handleUploadFile = async (): Promise<void> => {
    if (!selectedFile) {
      return;
    }

    const error = validatePdfFile(selectedFile);
    if (error) {
      setFileError(error);
      return;
    }

    if (!requiredFieldsComplete) {
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: 'Complete the required document fields before uploading.',
      });
      return;
    }

    setIsUploading(true);
    setUploadState({
      progress: 0,
      isCompleted: false,
      isFailed: false,
    });

    const progressInterval = setInterval(() => {
      setUploadState((prevState) => {
        if (prevState.progress < 0.9) {
          return {
            ...prevState,
            progress: Math.min(prevState.progress + 0.1, 0.9),
          };
        }
        return prevState;
      });
    }, 200);

    try {
      const uploadDocumentType = getLiteracyScreeningUploadDocumentType(
        documentType,
        screenerInvoice,
        rawDocumentTypeOptions
      );
      const schoolIdentifier = isSchoolLevelSelected ? school : '';
      const result = await dispatch(
        uploadLiteracyScreeningFileThunk({
          schoolYear: schoolYear || '',
          schoolIdentifier,
          leaIdentifier: leaIdentifier || '',
          file: selectedFile,
          documentType: uploadDocumentType,
        })
      );

      clearInterval(progressInterval);

      if (uploadLiteracyScreeningFileThunk.fulfilled.match(result)) {
        setUploadState({
          progress: 1,
          isCompleted: true,
          isFailed: false,
        });
        await refetchFiles();
        handleClearFile();
        resetForm({ values: EMPTY_UPLOAD_VALUES });
        setTimeout(() => {
          setUploadState({
            progress: 0,
            isCompleted: false,
            isFailed: false,
          });
        }, 1500);
      } else if (uploadLiteracyScreeningFileThunk.rejected.match(result)) {
        setUploadState({
          progress: 0,
          isCompleted: false,
          isFailed: true,
          error: mapUploadError(
            result.error?.message ||
            reduxError ||
            'Failed to upload file. Please try again.'
          ),
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (uploadError) {
      clearInterval(progressInterval);
      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: mapUploadError(
          uploadError instanceof Error
            ? uploadError.message
            : reduxError || 'Failed to upload file. Please try again.'
        ),
      });
    } finally {
      clearInterval(progressInterval);
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (
    fileDocument: LiteracyScreeningFileListItemModel
  ): Promise<void> => {
    if (!leaIdentifier) {
      return;
    }

    try {
      const blob = await downloadLiteracyScreeningFile(
        leaIdentifier,
        fileDocument.id
      );
      createDownloadLinkAndExecute(fileDocument.fileName, blob);
    } catch (downloadErr) {
      console.error('Download error:', downloadErr);
    }
  };

  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    if (!leaIdentifier) {
      setDeleteError('LEA identifier is required to delete files.');
      return;
    }

    const documentToDelete = filesToUse.find((file) => file.id === documentId);
    const documentName = documentToDelete?.fileName || 'this document';

    setOptions({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete "${getLastSegmentFromFilename(
        documentName
      )}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeleteError('');
        try {
          setIsDeleting(documentId);
          const result = await dispatch(
            deleteLiteracyScreeningFileThunk({
              leaId: leaIdentifier,
              id: documentId,
            })
          );

          if (deleteLiteracyScreeningFileThunk.fulfilled.match(result)) {
            await refetchFiles();
          } else if (deleteLiteracyScreeningFileThunk.rejected.match(result)) {
            setDeleteError(
              result.error?.message ||
              'Failed to delete file. Please try again.'
            );
          }

          return {
            status: 200,
            data: null,
            error: { type: '', title: '', status: 200, detail: '' },
            aborted: false,
          };
        } catch (err) {
          setDeleteError(
            err instanceof Error
              ? err.message
              : 'Failed to delete file. Please try again.'
          );
          return {
            status: 500,
            data: null,
            error: { type: '', title: '', status: 500, detail: '' },
            aborted: false,
          };
        } finally {
          setIsDeleting(null);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel: () => { },
    });

    await showConfirmation();
  };

  const saveDocumentReviewFields = async (
    file: LiteracyScreeningFileListItemModel,
    mdeStatus: string,
    comments: string
  ): Promise<boolean> => {
    if (!leaIdentifier) {
      return false;
    }

    const nextStatus = getLiteracyScreeningDisplayedMdeStatus(mdeStatus);
    const nextComments = comments.slice(
      0,
      LITERACY_SCREENING_COMMENTS_MAX_LENGTH
    );
    if (isLiteracyScreeningDeniedStatus(nextStatus) && !nextComments.trim()) {
      setCommentErrors((previous) => ({
        ...previous,
        [file.id]: VALIDATION_MESSAGE.LITERACY_SCREENING_COMMENTS_REQUIRED,
      }));
      return false;
    }

    setCommentErrors((previous) => ({
      ...previous,
      [file.id]: '',
    }));

    await dispatch(
      updateLiteracyScreeningDocumentThunk({
        id: file.id,
        model: {
          ...file,
          leaIdentifier,
          mdeStatus: nextStatus,
          comments: nextComments,
        },
      })
    );
    return true;
  };

  return (
    <div className="literacy-screening-upload-documents">
      <Panel panelType={PanelType.INFO} heading="Upload Documents">
        <div className="upload-documents-content">
          <p className="upload-description">From Section A: Signature Page</p>
          <div className="literacy-screening-upload-fields">
            <FormDropDown
              displayName="Document Type"
              field="documentType"
              options={fieldOptions.documentTypeOptions}
              disabled={uploadControlsDisabled}
              isClearable={!uploadControlsDisabled}
              placeholder="Select..."
              validationSchema={validationSchema}
              data-testid="literacy-screening-document-type"
              onChangeEvent={async ({ value }) => {
                const nextValue = typeof value === 'string' ? value : '';
                if (
                  !isLiteracyScreeningScreenerInvoiceDocumentType(
                    nextValue,
                    rawDocumentTypeOptions
                  )
                ) {
                  await setFieldValue('invoiceLevel', '');
                  await setFieldValue('school', '');
                  await setFieldValue('screenerInvoice', '');
                }
              }}
            />
            <FormDropDown
              displayName="Invoice Level"
              field="invoiceLevel"
              options={fieldOptions.invoiceLevelOptions}
              disabled={invoiceFieldsDisabled}
              isClearable={!invoiceFieldsDisabled}
              placeholder="Select..."
              validationSchema={validationSchema}
              classConfig={{ labelClasses: requiredLabelClass }}
              data-testid="literacy-screening-invoice-level"
              onChangeEvent={async ({ value }) => {
                const nextValue = typeof value === 'string' ? value : '';
                await setFieldValue('screenerInvoice', '');
                if (
                  !isLiteracyScreeningSchoolInvoiceLevel(
                    nextValue,
                    rawInvoiceLevelOptions
                  )
                ) {
                  await setFieldValue('school', '');
                }
              }}
            />
            <FormDropDown
              displayName="School"
              field="school"
              options={fieldOptions.schoolOptions}
              disabled={schoolFieldDisabled}
              isClearable={!schoolFieldDisabled}
              placeholder="Select..."
              validationSchema={validationSchema}
              classConfig={{ labelClasses: schoolRequiredLabelClass }}
              data-testid="literacy-screening-school"
              onChangeEvent={async () => {
                await setFieldValue('screenerInvoice', '');
              }}
            />
            <FormDropDown
              displayName="Screener Invoice"
              field="screenerInvoice"
              options={fieldOptions.screenerInvoiceOptions}
              disabled={screenerInvoiceFieldDisabled}
              isClearable={!screenerInvoiceFieldDisabled}
              placeholder="Select..."
              validationSchema={validationSchema}
              classConfig={{ labelClasses: screenerRequiredLabelClass }}
              data-testid="literacy-screening-screener-invoice"
            />
          </div>

          <div className="file-upload-section">
            {!isUploading && !selectedFile && (
              <div className="file-input-container">
                <div className="file-input-row">
                  <div
                    className={`file-input-wrapper ${fileError ? 'file-input-wrapper--error' : ''
                      }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      data-testid="literacy-screening-file-input"
                      multiple={false}
                      disabled={fileChooserDisabled}
                    />
                    <button
                      type="button"
                      className="choose-files-button"
                      onClick={handleChooseFiles}
                      data-testid="literacy-screening-choose-files-button"
                      disabled={fileChooserDisabled}
                    >
                      Choose File
                    </button>
                    <span className="no-file-chosen">No file chosen</span>
                  </div>
                  <div className="file-requirements">
                    <div>
                      PDF files only. Maximum file size:{' '}
                      {getMaxFileUploadSizeMB()} MB.
                    </div>
                    <div>
                      Filename length: {getMaxUploadFilenameLength()} characters
                      maximum.
                    </div>
                    <div>
                      Upload one PDF per Document Type. If a document contains
                      multiple pages (such as invoice(s), combine all pages into
                      a single PDF before uploading.
                    </div>
                  </div>
                </div>
                {fileError ? (
                  <div
                    className="file-error"
                    data-testid="literacy-screening-file-error"
                  >
                    {fileError}
                  </div>
                ) : null}
              </div>
            )}

            {!isUploading && selectedFile && !uploadState.isFailed && (
              <div className="file-selected-container">
                <div
                  className={`file-selected-item ${fileError ? 'file-selected-item--error' : ''
                    }`}
                >
                  <span className="file-name">{selectedFile.name}</span>
                  <button
                    type="button"
                    className="delete-file-button"
                    onClick={handleClearFile}
                    data-testid="literacy-screening-delete-file-button"
                    aria-label="Delete file"
                  >
                    <img src="/images/icon-trash.svg" alt="Delete" />
                  </button>
                </div>
                {fileError ? (
                  <div
                    className="file-error"
                    data-testid="literacy-screening-file-error"
                  >
                    {fileError}
                  </div>
                ) : null}
                <div className="file-actions">
                  <ActionButton
                    onClick={handleClearFile}
                    classes="button no-wrap-text cancel-button"
                    dataTestId="literacy-screening-clear-file-button"
                    disabled={disabled || isUploading}
                  >
                    Clear File
                  </ActionButton>
                  <ActionButton
                    onClick={handleUploadFile}
                    classes="button--secondary submit-button"
                    dataTestId="literacy-screening-upload-file-button"
                    disabled={
                      disabled ||
                      isUploading ||
                      !!fileError ||
                      !requiredFieldsComplete
                    }
                  >
                    Upload File
                  </ActionButton>
                </div>
              </div>
            )}

            {!isUploading && selectedFile && uploadState.isFailed && (
              <div className="file-selected-container">
                <div className="file-selected-item file-selected-item--error">
                  <span className="file-name">{selectedFile.name}</span>
                </div>
                {uploadState.error ? (
                  <div
                    className="file-error"
                    data-testid="literacy-screening-file-error"
                  >
                    {uploadState.error}
                  </div>
                ) : null}
                <div className="file-actions">
                  <ActionButton
                    onClick={handleTryAgain}
                    classes="button no-wrap-text cancel-button"
                    dataTestId="literacy-screening-try-again-button"
                    disabled={disabled || isUploading}
                  >
                    Try again
                  </ActionButton>
                </div>
              </div>
            )}

            {isUploading && selectedFile && (
              <div className="file-upload-progress">
                <FileUploadStatus
                  fileType={selectedFile.name}
                  percent={uploadState.progress}
                  isCompleted={uploadState.isCompleted}
                  isFailed={uploadState.isFailed}
                  error={uploadState.error}
                />
                <div className="progress-ruler"></div>
              </div>
            )}
          </div>
        </div>

        <div className="uploaded-documents-section">
          <h3 className="table-title">List of Uploaded documents</h3>
          {deleteError ? (
            <div
              className="delete-error"
              data-testid="literacy-screening-delete-error"
            >
              {deleteError}
            </div>
          ) : null}
          <div className="uploaded-documents-table-container">
            <table
              className="uploaded-documents-table"
              data-testid="literacy-screening-uploaded-documents-table"
            >
              <thead>
                <tr>
                  <th className="document-name-cell">Document Name</th>
                  <th className="document-details-cell">Document Details</th>
                  <th className="uploaded-by-date-cell">Uploaded By & Date</th>
                  <th className="mde-status-cell">MDE Status</th>
                  <th className="comments-cell">Comments</th>
                  <th className="actions-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.length > 0 ? (
                  uploadedFiles.map((file) => {
                    const isSupportingDocument =
                      isLiteracyScreeningSupportingDocumentType(
                        file.documentType,
                        rawDocumentTypeOptions,
                        rawScreenerInvoiceOptions
                      );
                    const canEditReviewFields =
                      !uploadControlsDisabled &&
                      (isStateAdmin() || isSPO()) &&
                      !isSupportingDocument;
                    const displayedStatus =
                      getLiteracyScreeningDisplayedMdeStatus(
                        statusDrafts[file.id] ?? file.mdeStatus
                      );
                    const isDenied =
                      isLiteracyScreeningDeniedStatus(displayedStatus);
                    const isPending =
                      displayedStatus ===
                      LITERACY_SCREENING_PENDING_APPROVAL_STATUS;
                    const commentValue =
                      commentDrafts[file.id] ?? file.comments ?? '';
                    const detailLines = getLiteracyScreeningDocumentDetailLines(
                      file,
                      rawDocumentTypeOptions,
                      rawScreenerInvoiceOptions
                    );

                    return (
                      <tr
                        key={file.id}
                        data-testid={`literacy-screening-document-row-${file.id}`}
                      >
                        <td className="document-name-cell">
                          <button
                            type="button"
                            className="document-name-link"
                            onClick={() => handleDownloadDocument(file)}
                            data-testid={`literacy-screening-download-document-${file.id}`}
                            aria-label={`Download ${getLastSegmentFromFilename(
                              file.fileName
                            )}`}
                            disabled={disabled}
                          >
                            {getLastSegmentFromFilename(file.fileName)}
                          </button>
                        </td>
                        <td className="document-details-cell">
                          <div className="document-details">
                            <span>{detailLines.primary}</span>
                            {detailLines.secondary ? (
                              <span>{detailLines.secondary}</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="uploaded-by-date-cell">
                          <div className="uploaded-by-date">
                            <span>{file.uploadedBy || 'Unknown'}</span>
                            <span>{formatUploadDate(file.uploadedDate)}</span>
                          </div>
                        </td>
                        <td className="mde-status-cell">
                          {isSupportingDocument ? (
                            file.mdeStatus || ''
                          ) : (
                            <select
                              className={`mde-status-select${isPending ? ' forced-required-label' : ''
                                }`}
                              value={displayedStatus}
                              disabled={!canEditReviewFields}
                              required={isPending}
                              data-testid={`literacy-screening-mde-status-${file.id}`}
                              onChange={(event) => {
                                const nextStatus = event.target.value;
                                setStatusDrafts((previous) => ({
                                  ...previous,
                                  [file.id]: nextStatus,
                                }));
                                void saveDocumentReviewFields(
                                  file,
                                  nextStatus,
                                  commentValue
                                );
                              }}
                            >
                              {mdeStatusOptions.map((option: SelectOption) => (
                                <option key={option.value} value={option.value}>
                                  {option.text}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="comments-cell">
                          <textarea
                            className={`comments-input${isDenied ? ' comments-input--required' : ''
                              }`}
                            value={commentValue}
                            disabled={!canEditReviewFields}
                            rows={3}
                            maxLength={LITERACY_SCREENING_COMMENTS_MAX_LENGTH}
                            required={isDenied}
                            aria-required={isDenied}
                            data-testid={`literacy-screening-comments-${file.id}`}
                            onChange={(event) => {
                              const nextValue = event.target.value.slice(
                                0,
                                LITERACY_SCREENING_COMMENTS_MAX_LENGTH
                              );
                              setCommentDrafts((previous) => ({
                                ...previous,
                                [file.id]: nextValue,
                              }));
                              if (nextValue.trim()) {
                                setCommentErrors((previous) => ({
                                  ...previous,
                                  [file.id]: '',
                                }));
                              }
                            }}
                            onBlur={(event) => {
                              const nextValue = event.target.value;
                              if (
                                nextValue === (file.comments || '') &&
                                displayedStatus ===
                                getLiteracyScreeningDisplayedMdeStatus(
                                  file.mdeStatus
                                )
                              ) {
                                return;
                              }
                              void saveDocumentReviewFields(
                                file,
                                displayedStatus,
                                nextValue
                              );
                            }}
                          />
                          {commentErrors[file.id] ? (
                            <div
                              className="file-error"
                              data-testid={`literacy-screening-comments-error-${file.id}`}
                            >
                              {commentErrors[file.id]}
                            </div>
                          ) : null}
                        </td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            className="delete-document-button"
                            onClick={() => handleDeleteDocument(file.id)}
                            disabled={
                              uploadControlsDisabled ||
                              cannotDeleteDocuments ||
                              isDeleting === file.id ||
                              (isRestrictedDocumentDeleteUser &&
                                !canDeleteLiteracyScreeningUploadedFile(file))
                            }
                            data-testid={`literacy-screening-delete-document-${file.id}`}
                            aria-label={`Delete ${getLastSegmentFromFilename(
                              file.fileName
                            )}`}
                          >
                            {isDeleting === file.id ? (
                              <div className="delete-loading-spinner">
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                              </div>
                            ) : (
                              <img src="/images/icon-trash.svg" alt="Delete" />
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="no-documents"
                      data-testid="no-documents"
                    >
                      No documents uploaded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
};

const LiteracyScreeningUploadDocuments = (
  props: LiteracyScreeningUploadDocumentsProps
): ReactElement => {
  const optionSets = useAppSelector(selectLiteracyScreeningOptionSets);
  const validationSchema = useMemo(
    () =>
      createLiteracyScreeningUploadValidationSchema(
        getEnabledLiteracyScreeningOptions(
          optionSets,
          LITERACY_SCREENING_DOCUMENT_OPTION_SETS.documentType
        ),
        getEnabledLiteracyScreeningOptions(
          optionSets,
          LITERACY_SCREENING_DOCUMENT_OPTION_SETS.invoiceLevel
        )
      ),
    [optionSets]
  );

  return (
    <Formik
      initialValues={EMPTY_UPLOAD_VALUES}
      onSubmit={noopSubmit}
      validationSchema={validationSchema}
      validateOnBlur
      validateOnChange
    >
      <LiteracyScreeningUploadDocumentsForm {...props} />
    </Formik>
  );
};

export default LiteracyScreeningUploadDocuments;
